import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckCircle2, Mail, ArrowRight, Loader2, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { PasswordStrength, getStrengthScore } from '@/components/auth/PasswordStrength'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'

export function SetupMailbox() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<any>(null)
  const [setupInfo, setSetupInfo] = useState<{ email: string } | null>(null)
  const [validating, setValidating] = useState(!!token)
  const [touched, setTouched] = useState({ password: false, confirm: false })

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing setup link.')
      setValidating(false)
      return
    }
    let active = true
    api.get('/mailboxes/setup/validate', { params: { token } })
      .then((response) => {
        if (active) setSetupInfo(response.data)
      })
      .catch((err) => {
        if (!active) return
        const detail = err.response?.data?.detail
        setError(typeof detail === 'string' ? detail : detail?.message || 'This setup link is invalid or has expired.')
      })
      .finally(() => {
        if (active) setValidating(false)
      })
    return () => { active = false }
  }, [token])

  const passwordError = useMemo(() => {
    if (!password) return 'Password is required'
    if (getStrengthScore(password) < 5) return 'Password does not meet all requirements'
    return ''
  }, [password])

  const confirmError = useMemo(() => {
    if (!confirm) return 'Please confirm your password'
    if (confirm !== password) return 'Passwords do not match'
    return ''
  }, [confirm, password])

  const canSubmit = !passwordError && !confirmError && !!token && !!setupInfo && !validating

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ password: true, confirm: true })
    if (!canSubmit) return

    setLoading(true)
    setError('')
    try {
      const res = await api.post('/mailboxes/setup', { token, password })
      setResult(res.data)
      setDone(true)
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : detail?.message || 'Failed to set password.')
    } finally {
      setLoading(false)
    }
  }

  if (done && result) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
          <CheckCircle2 className="h-7 w-7 text-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <h1 className="text-2xl font-bold tracking-tight">Mailbox ready</h1>
          <p className="text-sm text-muted-foreground">
            Password set for <strong>{result.email}</strong>.
          </p>
        </div>
        {result.client_setup_url && (
          <Button
            className="w-full"
            onClick={() => { window.location.href = result.client_setup_url }}
          >
            <Mail className="mr-2 h-4 w-4" />
            Set Up a Mail App
          </Button>
        )}
        {result.webmail_url && (
          <Button asChild variant="outline" className="w-full">
            <a href={result.webmail_url}>
              Open Webmail
              <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1.5">
        <h1 className="text-2xl font-bold tracking-tight">Choose a mailbox password</h1>
        <p className="break-all text-sm text-muted-foreground">
          {setupInfo ? setupInfo.email : 'Your mailbox'}
        </p>
      </div>

      {validating && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking setup link…
        </div>
      )}

      {!validating && (
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthAlert message={error} />}

        <fieldset disabled={!setupInfo} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <PasswordInput
            id="password"
            placeholder="At least 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            aria-invalid={touched.password && !!passwordError}
            aria-describedby="password-help"
            required
            autoFocus
          />
          {touched.password && passwordError ? (
            <p id="password-error" className="text-xs text-destructive">{passwordError}</p>
          ) : (
            <p id="password-help" className="text-xs text-muted-foreground">
              8+ characters with uppercase, lowercase, digit, and special character.
            </p>
          )}
          <PasswordStrength password={password} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm">Confirm Password</Label>
          <PasswordInput
            id="confirm"
            placeholder="Repeat password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
            aria-invalid={touched.confirm && !!confirmError}
            aria-describedby={touched.confirm && confirmError ? 'confirm-error' : undefined}
            required
          />
          {touched.confirm && confirmError && (
            <p id="confirm-error" className="text-xs text-destructive">{confirmError}</p>
          )}
        </div>

        <AuthSubmitButton loading={loading} disabled={!canSubmit}>
          Complete Setup
          <ArrowRight className="ml-2 h-4 w-4" />
        </AuthSubmitButton>
        </fieldset>
      </form>
      )}
    </div>
  )
}
